import { useRef, type JSX, type KeyboardEvent, type PointerEvent } from 'react';
import './components.css';

export interface SliderProps {
  value: number;
  min: number;
  max: number;
  step?: number;
  /** Larger step for PageUp/PageDown and Shift+arrow. Defaults to 10 steps. */
  bigStep?: number;
  onChange(value: number): void;
  /** Pointer released or keyboard change finished: close the undo step here. */
  onCommit?(): void;
  onReset?(): void;
  label: string;
  valueText?: string;
  vertical?: boolean;
  disabled?: boolean;
  /** Positions (in value units) of tick marks, e.g. unity gain. */
  marks?: readonly number[];
  /** False leaves a keyboard change open (no `onCommit`), for controls whose steps add up to one action. */
  commitOnKey?: boolean;
}

export function clampToStep(value: number, min: number, max: number, step: number): number {
  const clamped = Math.min(max, Math.max(min, value));
  if (!(step > 0)) return clamped;
  const snapped = min + Math.round((clamped - min) / step) * step;
  // Rounding in binary floating point leaves tails like 0.30000000000000004.
  return Math.min(max, Math.max(min, Number(snapped.toFixed(6))));
}

export function Slider(props: SliderProps): JSX.Element {
  const { value, min, max, step = 0, onChange, onCommit, onReset, label, valueText, vertical, disabled, marks } = props;
  const root = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);
  const span = max - min || 1;
  const ratio = Math.min(1, Math.max(0, (value - min) / span));

  const valueAt = (event: PointerEvent<HTMLDivElement>): number => {
    const rect = root.current!.getBoundingClientRect();
    const t = vertical
      ? 1 - (event.clientY - rect.top) / (rect.height || 1)
      : (event.clientX - rect.left) / (rect.width || 1);
    return clampToStep(min + Math.min(1, Math.max(0, t)) * span, min, max, step);
  };

  const onPointerDown = (event: PointerEvent<HTMLDivElement>): void => {
    if (disabled || event.button !== 0) return;
    dragging.current = true;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    event.currentTarget.focus();
    onChange(valueAt(event));
  };
  const onPointerMove = (event: PointerEvent<HTMLDivElement>): void => {
    if (dragging.current) onChange(valueAt(event));
  };
  const endDrag = (): void => {
    if (!dragging.current) return;
    dragging.current = false;
    onCommit?.();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (disabled) return;
    const small = step > 0 ? step : span / 100;
    const big = props.bigStep ?? small * 10;
    const delta =
      event.key === 'ArrowRight' || event.key === 'ArrowUp'
        ? event.shiftKey ? big : small
        : event.key === 'ArrowLeft' || event.key === 'ArrowDown'
          ? event.shiftKey ? -big : -small
          : event.key === 'PageUp'
            ? big
            : event.key === 'PageDown'
              ? -big
              : 0;
    let next: number | null = null;
    if (delta !== 0) next = clampToStep(value + delta, min, max, step);
    else if (event.key === 'Home') next = min;
    else if (event.key === 'End') next = max;
    if (next === null) return;
    event.preventDefault();
    event.stopPropagation();
    onChange(next);
    if (props.commitOnKey !== false) onCommit?.();
  };

  const percent = `${ratio * 100}%`;
  return (
    <div
      ref={root}
      className={['ui-slider', vertical ? 'vertical' : '', disabled ? 'disabled' : ''].join(' ')}
      role="slider"
      tabIndex={disabled ? -1 : 0}
      aria-label={label}
      aria-orientation={vertical ? 'vertical' : 'horizontal'}
      aria-valuemin={min}
      aria-valuemax={max}
      aria-valuenow={value}
      aria-valuetext={valueText}
      aria-disabled={disabled || undefined}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onDoubleClick={() => !disabled && onReset?.()}
      onKeyDown={onKeyDown}
    >
      <div className="ui-slider-track">
        <div className="ui-slider-fill" style={vertical ? { height: percent, left: 0 } : { width: percent }} />
      </div>
      {marks?.map((mark) => {
        const at = `${Math.min(1, Math.max(0, (mark - min) / span)) * 100}%`;
        return <div key={mark} className="ui-slider-mark" style={vertical ? { bottom: at } : { left: at }} />;
      })}
      <div className="ui-slider-thumb" style={vertical ? { bottom: percent } : { left: percent }} />
    </div>
  );
}
